import { createElement, useContext, useEffect } from 'react';
import { useBlocker, UNSAFE_DataRouterContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

function BlockerGate({ isDirty, message }: { isDirty: boolean; message: string }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  if (!dataRouter) return null;
  return createElement(BlockerEffect, { isDirty, message });
}

function BlockerEffect({ isDirty, message }: { isDirty: boolean; message: string }) {
  const blocker = useBlocker(isDirty);
  const { t } = useTranslation();
  const isBlocked = blocker.state === 'blocked';

  return createElement(
    Dialog,
    {
      open: isBlocked,
      onClose: () => { if (blocker.state === 'blocked') blocker.reset(); },
      'aria-labelledby': 'unsaved-changes-dialog-title',
    },
    createElement(DialogTitle, { id: 'unsaved-changes-dialog-title' }, t('common:unsavedChanges')),
    createElement(DialogContent, null,
      createElement(DialogContentText, null, message),
    ),
    createElement(
      DialogActions,
      null,
      createElement(
        Button,
        { onClick: () => { if (blocker.state === 'blocked') blocker.reset(); }, color: 'inherit' as const },
        t('common:cancel'),
      ),
      createElement(
        Button,
        {
          onClick: () => { if (blocker.state === 'blocked') blocker.proceed(); },
          variant: 'contained' as const,
          color: 'warning' as const,
          autoFocus: true,
        },
        t('common:confirm'),
      ),
    ),
  );
}

/**
 * Warns the user before leaving a route or refreshing the browser while the
 * form/page is dirty. The prompt is removed as soon as the dirty state becomes
 * false (e.g. after a successful save or explicit cancel/reset).
 *
 * Gracefully degrades when used outside a data router context (e.g. in tests
 * with MemoryRouter) — only the beforeunload listener is active in that case.
 */
export function useUnsavedChangesGuard(isDirty: boolean, message?: string) {
  const { t } = useTranslation();
  const prompt = message ?? t('common:unsavedChangesPrompt');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return createElement(BlockerGate, { isDirty, message: prompt });
}
